<template>
  <b-modal
    id="brochurePhotoPicker"
    size="lg"
    scrollable
    hide-footer
    :title="t('brochurePickerTitle')"
    :dir="isHebrew ? 'rtl' : 'ltr'"
  >
    <div :class="['brochure-picker', isHebrew ? 'text-right' : 'text-left']">

      <div v-if="loading" class="text-center py-5">
        <span class="spinner-border" role="status" aria-hidden="true"></span>
        <p class="mt-3 text-muted">{{ t('brochurePickerLoading') }}</p>
      </div>

      <div v-else-if="loadFailed" class="text-center py-5">
        <p class="text-muted">{{ t('brochurePickerLoadFailed') }}</p>
        <b-button variant="primary" @click="generateAutomatic">{{ t('brochurePickerUseAutomatic') }}</b-button>
      </div>

      <template v-else-if="options">
        <!-- Cover -->
        <section class="picker-section">
          <div class="picker-section-head">
            <div class="picker-section-title">{{ t('brochurePickerCover') }}</div>
            <div class="picker-count">{{ selection.cover.length }} / 1</div>
          </div>
          <div class="picker-grid">
            <div
              v-for="option in options.cover.options"
              :key="'cover-' + option.url"
              :class="['picker-photo', isCoverPicked(option.url) ? 'is-picked' : '']"
              :title="option.label"
              @click="pickCover(option.url)"
            >
              <img :src="option.thumb" :alt="option.label" loading="lazy" @error="useFullImage($event, option)">
              <span v-if="isCoverPicked(option.url)" class="picker-badge">✓</span>
              <span v-if="option.label" class="picker-label">{{ option.label }}</span>
            </div>
          </div>
          <div class="picker-hint">{{ t('brochurePickerCoverHint') }}</div>
        </section>

        <!-- Stays -->
        <section v-for="stay in options.stays" :key="stay.key" class="picker-section">
          <div class="picker-section-head">
            <div class="picker-section-title">
              <span class="picker-dates" dir="ltr">{{ stay.dates }}</span>
              <span v-if="stay.hotel"> · {{ stay.hotel }}</span>
              <small v-if="stay.location" class="text-muted"> {{ stay.location }}</small>
            </div>
            <div class="picker-count">{{ selection.stays[stay.key].length }} / {{ stay.max }}</div>
          </div>

          <div v-if="stay.options.length" class="picker-grid">
            <div
              v-for="option in stay.options"
              :key="stay.key + '-' + option.url"
              :class="[
                'picker-photo',
                pickOrder(stay.key, option.url) ? 'is-picked' : '',
                isStayFull(stay) && !pickOrder(stay.key, option.url) ? 'is-disabled' : ''
              ]"
              :title="option.label"
              @click="toggleStayPhoto(stay, option.url)"
            >
              <img :src="option.thumb" :alt="option.label" loading="lazy" @error="useFullImage($event, option)">
              <span v-if="pickOrder(stay.key, option.url)" class="picker-badge">{{ pickOrder(stay.key, option.url) }}</span>
              <span v-if="option.label" class="picker-label">{{ option.label }}</span>
            </div>
          </div>
          <div v-else class="picker-empty">{{ t('brochurePickerNoPhotos') }}</div>

          <div v-if="stay.options.length" class="picker-hint">
            {{ t('brochurePickerStayHint', { max: stay.max }) }}
          </div>
        </section>

        <div :class="['picker-footer', isHebrew ? 'flex-row-reverse' : '']">
          <b-button variant="outline-secondary" size="sm" @click="resetToAutomatic">
            {{ t('brochurePickerBackToAuto') }}
          </b-button>
          <div class="picker-footer-actions">
            <b-button variant="secondary" @click="close">{{ t('brochurePickerCancel') }}</b-button>
            <b-button variant="primary" @click="generate">
              <i class="fas fa-file-pdf"></i> {{ t('brochurePickerGenerate') }}
            </b-button>
          </div>
        </div>
      </template>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex';
import { translationMixin } from '@/mixins/translationMixin';

/**
 * Lets the ride manager choose the brochure's photos before it is generated.
 *
 * Opens with the automatic choice already selected. Emits `generate` with the
 * full selection, every stay included, and the server uses it exactly as shown:
 * the photo numbered 1 is the one in the large tile. Sending only the changes
 * would let the server re-sort the untouched stays, and the PDF would no longer
 * match what the picker showed.
 */
export default {
  name: 'BrochurePhotoPicker',
  mixins: [translationMixin],
  props: {
    rideId: { type: [String, Number], required: true }
  },
  data() {
    return {
      loading: false,
      loadFailed: false,
      options: null,
      selection: { cover: [], stays: {} },
      automatic: { cover: [], stays: {} }
    };
  },
  methods: {
    ...mapActions(['fetchBrochurePhotoOptions']),

    async open() {
      this.$bvModal.show('brochurePhotoPicker');
      this.loading = true;
      this.loadFailed = false;
      this.options = null;
      try {
        const data = await this.fetchBrochurePhotoOptions(this.rideId);
        if (!data || data.ServerMassageCode !== 'success') {
          throw new Error(data && data.ServerMassageCode);
        }
        this.options = data;
        this.automatic = this.selectionFrom(data);
        this.resetToAutomatic();
      } catch (error) {
        console.error('Failed to load brochure photos:', error);
        this.loadFailed = true;
      } finally {
        this.loading = false;
      }
    },

    close() {
      this.$bvModal.hide('brochurePhotoPicker');
    },

    selectionFrom(data) {
      const stays = {};
      data.stays.forEach(stay => { stays[stay.key] = [...stay.selected]; });
      return { cover: [...data.cover.selected], stays };
    },

    resetToAutomatic() {
      const stays = {};
      Object.keys(this.automatic.stays).forEach(key => { stays[key] = [...this.automatic.stays[key]]; });
      this.selection = { cover: [...this.automatic.cover], stays };
    },

    isCoverPicked(url) {
      return this.selection.cover.includes(url);
    },

    pickOrder(stayKey, url) {
      const index = this.selection.stays[stayKey].indexOf(url);
      return index === -1 ? 0 : index + 1;
    },

    isStayFull(stay) {
      return this.selection.stays[stay.key].length >= stay.max;
    },

    pickCover(url) {
      this.selection.cover = [url];
    },

    toggleStayPhoto(stay, url) {
      const picked = this.selection.stays[stay.key];
      const index = picked.indexOf(url);
      if (index !== -1) {
        picked.splice(index, 1);
      } else if (picked.length < stay.max) {
        picked.push(url);
      }
    },

    // Thumbnails are the Firebase `_300x300` copies, which not every upload has.
    useFullImage(event, option) {
      if (event.target.src !== option.url) {
        event.target.src = option.url;
      }
    },

    currentPicks() {
      const stays = {};
      Object.keys(this.selection.stays).forEach(key => { stays[key] = [...this.selection.stays[key]]; });
      const photos = { stays };
      if (this.selection.cover.length) {
        photos.cover = this.selection.cover[0];
      }
      return photos;
    },

    generate() {
      this.close();
      this.$emit('generate', this.currentPicks());
    },

    generateAutomatic() {
      this.close();
      this.$emit('generate', null);
    }
  }
};
</script>

<style scoped>
.picker-section {
  margin-bottom: 1.4rem;
}
.picker-section-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.5rem;
  gap: 0.5rem;
}
.picker-section-title {
  font-weight: 600;
  font-size: 0.95rem;
}
.picker-dates {
  color: #b08d4f;
  unicode-bidi: isolate;
}
.picker-count {
  font-size: 0.8rem;
  font-weight: 600;
  color: #b08d4f;
  white-space: nowrap;
}
.picker-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.picker-photo {
  position: relative;
  width: 132px;
  aspect-ratio: 16 / 9;
  border-radius: 5px;
  overflow: hidden;
  cursor: pointer;
  outline: 2px solid transparent;
  background: #eef1f5;
}
.picker-photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 30%;
  display: block;
}
.picker-photo.is-picked {
  outline: 3px solid #1d6fe0;
  outline-offset: 1px;
}
.picker-photo.is-disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.picker-badge {
  position: absolute;
  top: 5px;
  inset-inline-end: 5px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #1d6fe0;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 22px;
  text-align: center;
}
.picker-label {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 2px 6px;
  font-size: 0.65rem;
  color: #fff;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.picker-hint {
  margin-top: 0.4rem;
  font-size: 0.75rem;
  color: #7b8794;
}
.picker-empty {
  padding: 0.9rem;
  border: 1px dashed #c5ced8;
  border-radius: 5px;
  color: #9aa5b1;
  font-size: 0.8rem;
  text-align: center;
}
.picker-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding-top: 0.8rem;
  border-top: 1px solid #e5e9ef;
}
.picker-footer-actions {
  display: flex;
  gap: 0.5rem;
}
</style>
